import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle2, XCircle, MinusCircle, FileText, KeyRound, Tag, X } from "lucide-react";
import { RubricInputs, rubricTotal } from "@/components/RubricInputs";
import { FeedbackPdfUpload } from "@/components/FeedbackPdf";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TutorAttemptMark() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [keyMap, setKeyMap] = useState({}); // number -> letter
  const [tagMap, setTagMap] = useState({}); // number -> [tags]
  const [tagLib, setTagLib] = useState([]);
  const [tagInput, setTagInput] = useState({}); // number -> draft text
  const [saving, setSaving] = useState(false);
  const [saqMarks, setSaqMarks] = useState({});
  const [rubric, setRubric] = useState({});
  useEffect(() => {
    if (data?.format === "saq" || data?.format === "mixed") {
      const m = {};
      (data.format === "mixed" ? data.saq_questions : data.questions).forEach((q) => { if (q.awarded != null) m[q.qid] = q.awarded; });
      const rb = {};
      (data.saq_questions || []).forEach((q) => { if (q.rubric) rb[q.qid] = q.rubric; });
      setRubric(rb);
      setSaqMarks(m);
    }
  }, [data]);

  const load = async () => {
    const { data } = await api.get(`/tutor/attempts/${id}`);
    setData(data);
    const km = {}, tm = {};
    data.questions.forEach((q) => { if (q.correct_answer) km[q.number] = q.correct_answer; tm[q.number] = q.tags || []; });
    setKeyMap(km); setTagMap(tm);
  };
  useEffect(() => { load(); api.get("/tutor/tags").then((r) => setTagLib(r.data)).catch(() => {}); }, [id]);
  if (!data) return <Loading />;

  if (data.format === "saq") {
    const totalMax = data.questions.reduce((s, q) => s + (q.max_marks || 10), 0);
    const awarded = data.questions.reduce((s, q) => s + (Number(saqMarks[q.qid]) || 0), 0);
    const saveSaq = async () => {
      const marks = {};
      data.questions.forEach((q) => { marks[q.qid] = Number(saqMarks[q.qid]) || 0; });
      try {
        const { data: r } = await api.post(`/tutor/attempts/${id}/saq-mark`, { marks });
        toast.success(`Marked ${r.score}/${r.total} (${r.percentage}%)`);
        load();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
      <div className="animate-fade-up" data-testid="attempt-mark-page">
        <button onClick={() => navigate("/tutor/results")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to results</button>
        <div className="mb-5"><FeedbackPdfUpload kind="attempt" id={id} url={data.feedback_pdf_url} hint="Optional marked-up PDF for this student, shown with their results." onChange={(u) => setData((p) => ({ ...p, feedback_pdf_url: u }))} /></div>
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-[#1E1B2E]">{data.student_name}</h1>
            <p className="text-sm text-[#948EAB]">{data.title} · {data.program_name} · written answers</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right"><div className="text-xs font-semibold text-[#948EAB]">Score</div><div className="font-heading text-2xl font-extrabold text-[#5B21B6]" data-testid="saq-total">{awarded}/{totalMax}</div></div>
            <Button onClick={saveSaq} data-testid="save-saq" className="h-11 rounded-xl ember-gradient font-bold"><Save className="mr-1.5 h-4 w-4" /> Save marks</Button>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div className="ember-card overflow-hidden p-0">
            <div className="flex items-center gap-2 border-b border-[#F1EEFA] px-4 py-3 text-sm font-bold text-[#1E1B2E]"><FileText className="h-4 w-4 text-[#7C3AED]" /> Exam paper</div>
            {data.file_url ? <iframe title="exam" src={`${BACKEND}${data.file_url}`} className="h-[75vh] w-full" /> : <div className="p-10 text-center text-sm text-[#948EAB]">No document attached.</div>}
          </div>
          <div className="space-y-3">
            {data.questions.map((q) => (
              <div key={q.qid} data-testid={`saq-mark-row-${q.number}`} className="ember-card p-4">
                <div className="mb-1 text-sm font-bold text-[#1E1B2E]">{q.label || `Response ${q.number}`}</div>
                <p className="mb-3 whitespace-pre-wrap rounded-xl border border-[#EEEAF8] bg-[#FAF9FE] p-3 text-sm text-[#1E1B2E]" data-testid={`saq-answer-${q.number}`}>{q.your_answer || <span className="text-[#948EAB]">No answer</span>}</p>
                <div className="flex items-center gap-2">
                  <Label className="text-[#524B6B]">Marks</Label>
                  <Input type="number" min="0" max={q.max_marks || 10} data-testid={`saq-marks-${q.number}`} value={saqMarks[q.qid] ?? ""} onChange={(e) => setSaqMarks({ ...saqMarks, [q.qid]: e.target.value })} className="h-10 w-20 rounded-xl" />
                  <span className="text-sm font-semibold text-[#948EAB]">/ {q.max_marks || 10}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const letters = Array.from({ length: data.option_count || 4 }, (_, i) => String.fromCharCode(65 + i));
  const setKey = (num, l) => setKeyMap((m) => ({ ...m, [num]: m[num] === l ? "" : l }));

  const keyedCount = Object.values(keyMap).filter(Boolean).length;
  const answeredCount = data.questions.filter((q) => q.your_answer).length;
  // live correctness preview against current keyMap
  const liveCorrect = data.questions.filter((q) => keyMap[q.number] && q.your_answer && keyMap[q.number].toLowerCase() === q.your_answer.toLowerCase()).length;
  const livePct = data.questions.length ? Math.round((liveCorrect / data.questions.length) * 100) : 0;

  const addTag = (num, name) => {
    const t = (name || "").trim();
    if (!t) return;
    setTagMap((m) => ({ ...m, [num]: [...new Set([...(m[num] || []), t])] }));
    setTagInput((s) => ({ ...s, [num]: "" }));
  };
  const removeTag = (num, t) => setTagMap((m) => ({ ...m, [num]: (m[num] || []).filter((x) => x !== t) }));

  const saveTags = async () => {
    try {
      await api.put(`/homework/${data.homework_id}/question-tags`, { tags: tagMap });
      toast.success("Question tags saved");
      api.get("/tutor/tags").then((r) => setTagLib(r.data)).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(keyMap).forEach(([n, l]) => { if (l) payload[n] = l; });
      await api.put(`/homework/${data.homework_id}/answer-key`, { answer_key: payload });
      toast.success("Answer key saved — all attempts re-marked");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveMixedSaq = async () => {
    const marks = {};
    data.saq_questions.forEach((q) => { marks[q.qid] = Number(saqMarks[q.qid]) || 0; });
    try {
      const { data: r } = await api.post(`/tutor/attempts/${id}/saq-mark`, { marks, rubric });
      toast.success(`Short answers marked ${r.score}/${r.total}. Overall ${r.percentage}%`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up" data-testid="attempt-mark-page">
      <datalist id="taglib">{tagLib.map((t) => <option key={t} value={t} />)}</datalist>
      <button onClick={() => navigate("/tutor/results")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to results
      </button>
      <div className="mb-5"><FeedbackPdfUpload kind="attempt" id={id} url={data.feedback_pdf_url} hint="Optional marked-up PDF for this student, shown with their results." onChange={(u) => setData((p) => ({ ...p, feedback_pdf_url: u }))} /></div>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-[#1E1B2E]">{data.student_name}</h1>
          <p className="text-sm text-[#948EAB]">{data.title} · {data.program_name} · {data.section_name}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs font-semibold text-[#948EAB]">Score</div>
            <div className="font-heading text-2xl font-extrabold text-[#5B21B6]" data-testid="mark-score">{livePct}%</div>
            <div className="text-xs text-[#948EAB]">{liveCorrect}/{data.questions.length} correct</div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-answer-key" className="h-11 rounded-xl ember-gradient font-bold">
            <Save className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save key & re-mark"}
          </Button>
          <Button onClick={saveTags} variant="outline" data-testid="save-tags" className="h-11 rounded-xl font-bold"><Tag className="mr-1.5 h-4 w-4" /> Save tags</Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        {/* PDF */}
        <div className="ember-card overflow-hidden p-0" data-testid="mark-pdf">
          <div className="flex items-center gap-2 border-b border-[#F1EEFA] px-4 py-3 text-sm font-bold text-[#1E1B2E]"><FileText className="h-4 w-4 text-[#7C3AED]" /> Exam paper</div>
          {data.file_url
            ? <iframe title="exam" src={`${BACKEND}${data.file_url}`} className="h-[75vh] w-full" />
            : <div className="p-10 text-center text-sm text-[#948EAB]">No document attached.</div>}
        </div>

        {/* Answer sheet + key */}
        <div>
          <div className="mb-3 flex items-center justify-between rounded-xl bg-[#F5F3FF] px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-bold text-[#5B21B6]"><KeyRound className="h-4 w-4" /> {keyedCount}/{data.questions.length} keyed</div>
            <div className="text-xs font-semibold text-[#948EAB]">{answeredCount} answered by student</div>
          </div>
          {!data.has_key && keyedCount === 0 && (
            <div className="mb-3 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-xs font-semibold text-[#B45309]">
              No answer key yet. Enter the correct option for each question below, then save to mark this and every other student's paper.
            </div>
          )}
          <div className="space-y-2">
            {data.questions.map((q) => {
              const chosen = q.your_answer;
              const key = keyMap[q.number] || "";
              const isCorrect = key && chosen && key.toLowerCase() === chosen.toLowerCase();
              return (
                <div key={q.qid} data-testid={`mark-row-${q.number}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#EEEAF8] px-3 py-2">
                  <span className="w-8 shrink-0 font-heading font-bold text-[#1E1B2E]">Q{q.number}</span>
                  <span className="shrink-0 text-xs font-semibold text-[#948EAB]">
                    Student: <span className={`ml-1 rounded-md px-1.5 py-0.5 font-bold ${chosen ? "bg-[#EDE9FE] text-[#5B21B6]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`} data-testid={`student-ans-${q.number}`}>{chosen || "—"}</span>
                  </span>
                  <div className="flex flex-1 flex-wrap items-center gap-1.5">
                    <span className="text-xs font-semibold text-[#948EAB]">Key:</span>
                    {letters.map((l) => {
                      const on = key === l;
                      return (
                        <button key={l} data-testid={`key-${q.number}-${l}`} onClick={() => setKey(q.number, l)}
                          className={`h-7 w-7 rounded-lg border text-xs font-bold transition-colors ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#EEEAF8] text-[#524B6B] hover:border-[#C4B5FD]"}`}>{l}</button>
                      );
                    })}
                  </div>
                  <span className="w-5 shrink-0" data-testid={`mark-state-${q.number}`}>
                    {!key ? <MinusCircle className="h-5 w-5 text-[#D1D5DB]" />
                      : isCorrect ? <CheckCircle2 className="h-5 w-5 text-[#059669]" />
                      : <XCircle className="h-5 w-5 text-[#E11D48]" />}
                  </span>
                  <div className="flex w-full flex-wrap items-center gap-1.5 pl-9">
                    <Tag className="h-3.5 w-3.5 text-[#948EAB]" />
                    {(tagMap[q.number] || []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#EDE9FE] px-2 py-0.5 text-[11px] font-bold text-[#5B21B6]" data-testid={`tag-${q.number}-${t}`}>
                        {t}<button onClick={() => removeTag(q.number, t)} className="text-[#7C3AED] hover:text-[#E11D48]"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                    <input list="taglib" data-testid={`tag-input-${q.number}`} value={tagInput[q.number] || ""}
                      onChange={(e) => setTagInput((s) => ({ ...s, [q.number]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(q.number, tagInput[q.number]); } }}
                      placeholder="add tag…" className="h-7 w-28 rounded-lg border border-[#EEEAF8] px-2 text-xs outline-none focus:border-[#C4B5FD]" />
                  </div>
                </div>
              );
            })}
          </div>
          {data.format === "mixed" && data.saq_questions?.length > 0 && (
            <div className="mt-6" data-testid="mixed-saq-marking">
              <div className="mb-3 flex items-center justify-between rounded-xl bg-orange-50 px-4 py-3">
                <div className="text-sm font-bold text-orange-700">Written parts · {data.saq_questions.reduce((s, q) => s + (q.type === "extended" ? rubricTotal(rubric[q.qid]) : Number(saqMarks[q.qid]) || 0), 0)}/{data.saq_questions.reduce((s, q) => s + (q.max_marks || 10), 0)}</div>
                <Button onClick={saveMixedSaq} data-testid="save-saq" className="h-9 rounded-xl ember-gradient text-xs font-bold"><Save className="mr-1.5 h-4 w-4" /> Save written marks</Button>
              </div>
              <div className="space-y-3">
                {data.saq_questions.map((q) => (
                  <div key={q.qid} data-testid={`saq-mark-row-${q.number}`} className="rounded-xl border border-[#EEEAF8] p-3">
                    <div className="mb-1 text-sm font-bold text-[#1E1B2E]">Q{q.number}{q.label && q.label !== `Q${q.number}` ? ` · ${q.label}` : ""}</div>
                    <p className="mb-2 whitespace-pre-wrap rounded-lg bg-[#FAF9FE] p-2.5 text-sm text-[#1E1B2E]" data-testid={`saq-answer-${q.number}`}>{q.your_answer || <span className="text-[#948EAB]">No answer</span>}</p>
                    {q.type === "extended" ? (
                      <RubricInputs value={rubric[q.qid] || {}} onChange={(v) => setRubric({ ...rubric, [q.qid]: v })} n={q.number} />
                    ) : (
                    <div className="flex items-center gap-2">
                      <Label className="text-[#524B6B]">Marks</Label>
                      <Input type="number" min="0" max={q.max_marks || 10} data-testid={`saq-marks-${q.number}`} value={saqMarks[q.qid] ?? ""} onChange={(e) => setSaqMarks({ ...saqMarks, [q.qid]: e.target.value })} className="h-9 w-20 rounded-xl" />
                      <span className="text-sm font-semibold text-[#948EAB]">/ {q.max_marks || 10}</span>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
